import store from 'app/store/store';
import { getLessonItems, getLessonItemV3 } from 'core/crud/lesson.crud';
import { validateLessonItem, validateLessonItemV2 } from 'core/models/lessonItem.model';
import { selectors as organizationSelectors } from 'core/store/organization.duck';
import { selectors as systemSettingsSelectors } from 'core/store/systemSettings.duck';
import { getGuidCourseByTemplate } from 'core/utils/course';
import { normalizeJob } from 'core/utils/jobs';
import {
  addUserInfoToJob,
  canUseTangerineViewer,
  canUseTangerineViewerInPreview,
  getAssignedUsersToLessonItem,
  getRouteProjectionByContentType,
  getTypeNameByContentType,
  getUsePdfTron,
  isDueDate,
} from 'core/utils/lessonItems';
import { isStudent } from 'core/utils/user';
import { CLIENT } from 'core/variables/constant';
import i18next from 'i18next';

// Campos que la UI deriva del tipo de contenido (visor embebido, nombre del tipo, ruta de proyección…).
// Dependen solo de `content_type_guid`/`content_guid`, así que valen igual para el payload v3 y el v2. El `icon`
// lo resuelve cada validador del modelo (en v3 con prioridad del tipo didáctico), por eso no se deriva aquí.
function getContentDerivedFields(data) {
  const contentTypeGuid = data?.content_type_guid;
  // Sin nombre mapeado (p. ej. CTTY_MINTBLOCK) se devuelve '' para que la UI caiga a su fallback
  // («Instrucciones») en vez de pedir la clave `lesson:undefined` y pintar ese literal.
  const typeNameKey = contentTypeGuid ? getTypeNameByContentType(contentTypeGuid) : '';
  return {
    typeName: typeNameKey ? i18next.t(`lesson:${typeNameKey}`) : '',
    routeProjection: getRouteProjectionByContentType(data?.content_guid, contentTypeGuid),
    usePdfTron: getUsePdfTron(contentTypeGuid),
    isDueDate: isDueDate(contentTypeGuid),
    useTangerineViewer: canUseTangerineViewer(contentTypeGuid),
    useTangerineViewerInPreview: canUseTangerineViewerInPreview(contentTypeGuid),
  };
}

async function getStudentJob(jobs, lessonItemGuid, courseGuid) {
  const studentJob = normalizeJob(jobs?.[0]);
  if (studentJob) return studentJob;
  const v2Response = await getLessonItems(lessonItemGuid, courseGuid);
  return normalizeJob(v2Response?.data?.data?.job) ?? null;
}

export async function getLessonItemService(lessonItemGuid, lessonGuid, course, currentUser) {
  // Lectura vía selectores para que el override dev-only de tenant (`__tenantOverride`) alcance esta rama.
  const state = store.getState();
  const enableAdvancedProgramming = systemSettingsSelectors.getSystemSettings(state)?.enable_advanced_programming === 1;
  const client = organizationSelectors.getClient(state);
  if (
    enableAdvancedProgramming ||
    client === CLIENT.TANGERINE ||
    client === CLIENT.SANTILLANA_BRASIL_DEMO ||
    client === CLIENT.SANTILLANA_BRASIL_COMPARTILHA ||
    client === CLIENT.SANTILLANA_BRASIL_UNOI_ON_PREM ||
    client === CLIENT.SANTILLANA_BRASIL_COMPARTILHA_ON_PREM
  ) {
    const courseGuid = course?.guid;
    try {
      const { data } = await getLessonItemV3(lessonItemGuid, lessonGuid, courseGuid);
      const job = isStudent(currentUser) ? await getStudentJob(data?.data?.jobs, lessonItemGuid, courseGuid) : null;

      return {
        ...validateLessonItem(data?.data),
        assignedUsers: getAssignedUsersToLessonItem(data?.data, course),
        allUsers: addUserInfoToJob(data?.data?.jobs, course?.users, data?.data?.lesson_item_guid, courseGuid),
        jobs: addUserInfoToJob(data?.data?.jobs, getAssignedUsersToLessonItem(data?.data, course), data?.data?.lesson_item_guid, courseGuid),
        job,
        ...getContentDerivedFields(data?.data),
      };
    } catch (error) {
      return Promise.reject({
        status: error?.response?.status || 500,
        message: 'error',
        error,
      });
    }
  } else {
    const response = await getLessonItems(lessonItemGuid, course?.guid);
    const data = response?.data?.data;
    // Mismo saneado que hace `getLessonItems` antes de llamar a la API: sin el `?` y los query params.
    const requestedGuid = lessonItemGuid?.split('?')[0];
    // La fila v2 se lleva al mismo contrato que produce `validateLessonItem` para la v3 (TAN-10512).
    const lessonItem = validateLessonItemV2(data, requestedGuid);
    const assignedUsers = getAssignedUsersToLessonItem(data, course);
    const courseGuidByTemplate = getGuidCourseByTemplate(course);
    return {
      ...lessonItem,
      assignedUsers,
      allUsers: addUserInfoToJob(data?.jobs, course?.users, lessonItem.guid, courseGuidByTemplate),
      jobs: addUserInfoToJob(data?.jobs, assignedUsers, lessonItem.guid, courseGuidByTemplate),
      ...getContentDerivedFields(data),
    };
  }
}
